import React from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import { debounce, isEqual } from 'lodash';
import Icon from 'common/components/core/icon';
import { Loader } from 'common/components/core/loader/Loader';
import LatLngUtils from 'common/utils/LatLngUtils';
import ColorUtil from 'common/utils/ColorUtil';
import { withRouter } from 'common/components/core/router';
import { connect } from 'react-redux';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import uuidv4 from 'uuid/v4';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { placesAutocompleteApi, placeDetailsApi } from 'common/middleware/api';
import Select, { components } from 'react-select';
import { MlFontIconLoader } from 'common/components/core/loader';
import { InputIconView } from 'common/components/core/input';
import { MlFontIcon } from 'common/components/core/font_icon';
import coreIds from 'common/tests/core/coreIds.json';
import { dashboardColors } from 'common/enums/ColorEnum';

import theme from './PlacesSearchBox.scss';

const getOptionValueLabel = ({ description }) => description;

class PlacesSearchBox extends React.PureComponent {
    static getDerivedStateFromProps(props, state) {
        if (
            state &&
            props.selectedAddress !== undefined &&
            props.selectedAddress !== state.selectedAddress
        ) {
            return {
                value: props.selectedAddress,
                selectedAddress: props.selectedAddress
            };
        }
        return null;
    }

    constructor(props) {
        super(props);
        this.state = {
            value: props.selectedAddress ? props.selectedAddress : '',
            activeCard: null,
            searchResults: null,
            sessionToken: this.getNewSessionToken(),
            selectedAddress: props.selectedAddress // keeping copy for gdsfp
        };
        this.debouncedSearch = debounce(this.search, 300);
        this.selectRef = React.createRef(null);
    }

    getNewSessionToken = () => {
        return uuidv4();
    };

    filterCachedSearchList = (cachedSearchList, state = this.state) => {
        const { value: searchValue } = state;
        const { filterCachedSearchList } = this.props;
        const filteredList = filterCachedSearchList
            ? filterCachedSearchList(cachedSearchList, searchValue)
            : cachedSearchList.filter(cachedSearchResult => {
                  var cachedSearchResultName = cachedSearchResult.name;
                  var cachedSearchResultAddress = cachedSearchResult.address;
                  var cachedSearchResultDescription =
                      (cachedSearchResultName
                          ? `${cachedSearchResultName}, `
                          : '') +
                      (cachedSearchResultAddress
                          ? cachedSearchResultAddress
                          : '');

                  var result = searchValue
                      ? cachedSearchResultDescription
                            .toLowerCase()
                            .indexOf(searchValue.toLowerCase()) > -1
                      : true;
                  return result;
              });
        return filteredList.map(cachedSearchResult => {
            const {
                name,
                address,
                lat,
                lng,
                type,
                target
            } = cachedSearchResult;
            return {
                description: `${name || ''} ${name && address ? ',' : ''} ${
                    address || ''
                }`,
                lat: Number(lat),
                lng: Number(lng),
                isCached: true,
                type,
                target
            };
        });
    };

    getRadiusAndCenter = () => {
        const {
            getRadiusAndCenter,
            useDefaultCenter,
            defaultCountryCenter
        } = this.props;
        let centerLatLng;
        let biasRadius;
        if (useDefaultCenter) {
            centerLatLng = defaultCountryCenter;
        }

        if (getRadiusAndCenter) {
            const { center, radius } = getRadiusAndCenter();
            biasRadius = radius;
            centerLatLng = center;
        }

        if (!centerLatLng) {
            throw new Error(
                'getRadiusAndCenter must return a latLng for autocomplete API bias'
            );
        }
        return { center: centerLatLng, radius: biasRadius };
    };

    search = inputValue => {
        const { t, clientId } = this.props;
        const { sessionToken, value } = this.state;
        var goToLatLng = {};
        var searchValue = inputValue ? inputValue : value;
        const isLatLng = LatLngUtils.isLatLng(searchValue);
        if (isLatLng) {
            var latLng = searchValue.split(',');
            goToLatLng = {
                description: t('useCoordinates', { value: searchValue }),
                lat: Number(latLng[0]),
                lng: Number(latLng[1]),
                isLatLng: true
            };
        }
        var { cachedSearchList } = this.props;
        var filteredCachedSearchList;
        if (cachedSearchList && cachedSearchList.length > 0) {
            filteredCachedSearchList = this.filterCachedSearchList(
                cachedSearchList
            );
        }
        if (searchValue) {
            const params = { clientId };
            let body = {
                text: searchValue,
                sessionToken
            };
            const { center, radius } = this.getRadiusAndCenter();
            // Calculate radius (in meters).
            body = { ...body, latLng: center };
            if (radius) {
                body = { ...body, radius };
            }
            this.setState({ prevRequestBody: body }, () => {
                this.fetchResultsFromAutocompleteApi(
                    params,
                    body,
                    goToLatLng,
                    isLatLng,
                    filteredCachedSearchList
                );
            });
        } else {
            this.setState(prevState => {
                if (prevState.focus && filteredCachedSearchList) {
                    return { searchResults: filteredCachedSearchList };
                }
                return {
                    searchResults: null,
                    prevRequestBody: null,
                    loading: false
                };
            });
        }
    };

    fetchResultsFromAutocompleteApi = (
        params,
        body,
        goToLatLng,
        isLatLng,
        filteredCachedSearchList
    ) => {
        placesAutocompleteApi
            .post(params, body)
            .then(data => {
                const { prevRequestBody, value: inputValue } = this.state;
                // Compare prevRequestBody and current request body.
                // Update search results only if body of successful response matches prevRequestBody
                // If an API respone arrives after input has been emptied, discard it
                if (inputValue && isEqual(prevRequestBody, body)) {
                    const { places } = data;
                    let searchResults = [];
                    if (places.length > 0) {
                        searchResults = places;

                        // modifying description field -> name + description
                        searchResults = searchResults.map(value => {
                            value.description = `${value.name}, ${value.description}`;
                            return value;
                        });
                    }
                    if (isLatLng) {
                        searchResults = [...searchResults, goToLatLng];
                    }
                    if (
                        filteredCachedSearchList &&
                        filteredCachedSearchList.length > 0
                    ) {
                        searchResults = [
                            ...filteredCachedSearchList,
                            ...searchResults
                        ];
                    }
                    this.setState({ searchResults, loading: false });
                }
            })
            .catch(error => {
                this.setState({
                    searchResults: null,
                    loading: false,
                    error
                });
            });
    };

    handleChange = (name, { action }) => {
        if (action === 'input-change') {
            this.setState({ value: name, loading: !!name }, () => {
                this.debouncedSearch(name);
            });
        }
    };

    onPlacesClear = () => {
        const { onPlacesClear } = this.props;
        this.setState({ value: '' }, () => {
            onPlacesClear & onPlacesClear();
        });
    };

    handleBlur = () => {
        const { onBlur } = this.props;
        this.setState({ focus: false });
        onBlur && onBlur();
    };

    handleFocus = () => {
        const { cachedSearchList, onFocus } = this.props;
        this.setState({ focus: true }, this.makeSelectedOptionEditable);
        if (cachedSearchList && cachedSearchList.length > 0) {
            const filteredCachedSearchList = this.filterCachedSearchList(
                cachedSearchList,
                {}
            );
            this.setState({ searchResults: filteredCachedSearchList });
        }

        if (onFocus) {
            onFocus();
        }
    };
    selectLocation = selectedItem => {
        const {
            clientId,
            onPlacesChanged,
            onPlacesClear,
            cachedSearchList
        } = this.props;
        if (selectedItem === null) {
            // backspace or clear indicator click
            this.setState({ value: '' }, () => {
                if (onPlacesClear) {
                    onPlacesClear();
                }
                this.handleFocus();
            });
            return;
        }

        const {
            placeId,
            isCached,
            lat,
            lng,
            description: originalDescription,
            target,
            type,
            isLatLng
        } = selectedItem;
        const { sessionToken } = this.state;
        let metadata = {};
        if (isCached) {
            metadata = { target, type };
        }
        let description = originalDescription;
        if (isLatLng) {
            description = `${lat}, ${lng}`;
        }
        if (placeId) {
            placeDetailsApi
                .fetch({ placeId, clientId, sessionToken })
                .then(place => {
                    const { latLng, formattedAddress } = place;
                    var { lat: newLat, lng: newLng } = latLng;
                    this.setState({
                        sessionToken: this.getNewSessionToken()
                    });
                    onPlacesChanged(newLat, newLng, formattedAddress);
                })
                .catch(error => {
                    this.setState({
                        error
                    });
                });
        } else {
            onPlacesChanged(lat, lng, description, metadata);
        }

        this.setState(({ searchResults }) => ({
            value: selectedItem.description,
            searchResults: cachedSearchList
                ? this.filterCachedSearchList(cachedSearchList, {})
                : searchResults
        }));
    };

    removeSearchResults = () => {
        this.setState({ activeCard: null, searchResults: null });
    };

    renderNoResults = () => {
        const { theme, t } = this.props;
        const { value } = this.state;
        return value ? (
            <div className={classnames(theme.result, theme.noResults)}>
                {t('noResultsFound')}
            </div>
        ) : null;
    };

    renderLoading = () => {
        const { theme } = this.props;
        return (
            <div className={classnames(theme.result, theme.loading)}>
                <Loader active theme={theme} />
            </div>
        );
    };

    renderSelectControl = controlProps => {
        const {
            theme,
            highlightColor,
            showErrorIcon,
            error,
            info,
            isInputLoading,
            iconClassName
        } = this.props;

        let { icon } = this.props;

        if (icon && typeof icon === 'string') {
            icon = (
                <MlFontIcon
                    iconClass={icon}
                    className={classnames(theme.fontIcon, iconClassName)}
                />
            );
        }
        return (
            <div
                className={classnames(theme.controlWrapper, {
                    [theme.showErrorIcon]: showErrorIcon,
                    [theme.focused]: controlProps.isFocused
                })}
            >
                {!!icon && (
                    <span className={classnames(theme.controlLeftIcon)}>
                        {icon}
                    </span>
                )}
                <components.Control
                    {...controlProps}
                    className={classnames(theme.control, {
                        [theme.withLeftIcon]: !!icon,
                        [theme.focused]: controlProps.isFocused,
                        [theme[`${highlightColor}Border`]]: highlightColor
                    })}
                />
                <InputIconView
                    theme={theme}
                    showErrorIcon={showErrorIcon}
                    error={error}
                    info={info}
                    isInputLoading={isInputLoading}
                />
            </div>
        );
    };

    renderPlaceholder = placeholderProps => {
        const { theme } = this.props;
        return (
            <div testId={coreIds.placesSearchBox.placeholder}>
                <components.Placeholder
                    {...placeholderProps}
                    className={classnames(theme.placeholder)}
                />
            </div>
        );
    };

    renderOption = optionProps => {
        const { theme, resultIcon, resultIcons } = this.props;
        const { target, isCached, type, isLatLng } = optionProps.data;
        let leftIconColor = null;
        let leftIcon = isCached
            ? resultIcon
                ? resultIcon
                : 'homebase'
            : 'location-icon';
        if (resultIcons && isCached) {
            leftIcon = resultIcons[type];
            leftIconColor =
                target && type === 'geometry'
                    ? ColorUtil.getZoneMapsColorName(target.getStyles().color)
                    : null;
        }

        return (
            <div
                testId={coreIds.placesSearchBox.option}
                className={classnames(theme.searchResult, {
                    [theme.latLng]: isLatLng,
                    [theme.isCardFocused]: optionProps.isFocused,
                    [theme.isSelected]: optionProps.isSelected
                })}
            >
                {leftIcon && (
                    <MlFontIconLoader
                        loading={false}
                        className={classnames(theme.leftIcon)}
                        color={leftIconColor}
                        iconClass={leftIcon}
                        theme={theme}
                    />
                )}
                <components.Option
                    {...optionProps}
                    className={classnames(theme.option)}
                />
            </div>
        );
    };

    renderMenu = menuProps => {
        const { theme } = this.props;
        const { searchResults } = this.state;
        return (
            <div testId={coreIds.placesSearchBox.menu}>
                <components.Menu
                    {...menuProps}
                    className={classnames(theme.searchResults, {
                        [theme.noResults]: !(
                            searchResults && searchResults.length
                        )
                    })}
                />
            </div>
        );
    };

    renderClearIndicator = indicatorProps => {
        const {
            innerProps: { ref, ...restInnerProps }
        } = indicatorProps;
        return (
            <div
                {...restInnerProps}
                ref={ref}
                className={classnames(theme.clearIcon)}
            >
                <Icon theme={theme} icon="cancel-thin" />
            </div>
        );
    };

    renderValueContainer = valueContainerProps => {
        const { theme } = this.props;
        return (
            <components.ValueContainer
                {...valueContainerProps}
                className={classnames(theme.valueContainer)}
            />
        );
    };

    filterOption = () => true; // removing react-select filtering logic as that we are doing already

    /**
     * When input focuses will change select input elem value
     * This will make selected option editable
     * This is hackie solution suggested here https://github.com/JedWatson/react-select/issues/1558
     */
    makeSelectedOptionEditable = () => {
        const { value } = this.state;
        if (value) {
            this.selectRef.current.select.handleInputChange({
                currentTarget: { value }
            });
        }
    };

    /*
        for making option editable after selection
        because we are injecting value to input on focus
        we need to blur() so that onFocus is triggered on next click
        (as mentioned in this comment: https://github.com/JedWatson/react-select/issues/1558#issuecomment-580961402)
    */
    handleOnMenuClose = () => {
        this.selectRef.current?.blur();
    };

    render() {
        const {
            theme,
            placeholder,
            label,
            icon,
            autoFocus,
            disabled,
            className,
            fullWidth,
            portal,
            maxMenuHeight,
            'data-test-id': dataTestId
        } = this.props;

        const { searchResults, loading, value } = this.state;

        const listActive = searchResults || loading;
        return (
            <div
                className={classnames(theme.searchWrap, className, {
                    [theme.searchOpen]: listActive,
                    [theme.fullWidth]: fullWidth
                })}
                data-react-toolbox="places-search"
                data-test-id={dataTestId}
            >
                {label && (
                    <label className={classnames(theme.label)}>{label}</label>
                )}
                <Select
                    className={classnames(theme.selectWrapper, theme.input)}
                    placeholder={placeholder}
                    onInputChange={!disabled ? this.handleChange : null}
                    getOptionLabel={getOptionValueLabel}
                    getOptionValue={getOptionValueLabel}
                    isDisabled={disabled}
                    isLoading={loading}
                    autoFocus={autoFocus}
                    isClearable
                    styles={{
                        menuPortal: defaultStyles => ({
                            ...defaultStyles,
                            zIndex: 99999
                        }),
                        option: defaultStyles => ({
                            ...defaultStyles,
                            background: 'transparent'
                        }),
                        menuList: defaultStyles => ({
                            ...defaultStyles,
                            padding: 0
                        }),
                        menu: (defaultStyles, { options }) => {
                            return {
                                ...defaultStyles,
                                boxShadow: options.length
                                    ? '0 0.2rem 0.3rem 0 rgba(0,0,0,0.3)'
                                    : 'initial',
                                zIndex: 10,
                                borderRadius: '0.2rem',
                                borderTop: `0.1rem solid ${dashboardColors.paleGreyThree.code}`,
                                marginTop: 0
                            };
                        },
                        valueContainer: defaultStyles =>
                            Object.assign(
                                {},
                                defaultStyles,
                                icon
                                    ? { paddingLeft: 0, lineHeight: '2rem' }
                                    : { lineHeight: '2rem' }
                            )
                    }}
                    components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                        Control: this.renderSelectControl,
                        Option: this.renderOption,
                        NoOptionsMessage: this.renderNoResults,
                        Menu: this.renderMenu,
                        ClearIndicator: this.renderClearIndicator,
                        LoadingMessage: this.renderLoading,
                        LoadingIndicator: () => null,
                        Placeholder: this.renderPlaceholder,
                        ValueContainer: this.renderValueContainer
                    }}
                    options={loading ? [] : searchResults || []}
                    menuPortalTarget={portal ? document.body : undefined}
                    onFocus={!disabled ? this.handleFocus : null}
                    onBlur={!disabled ? this.handleBlur : null}
                    onChange={this.selectLocation}
                    value={!!value && { description: value }}
                    filterOption={this.filterOption}
                    ref={this.selectRef}
                    onMenuClose={this.handleOnMenuClose}
                    maxMenuHeight={maxMenuHeight}
                />
            </div>
        );
    }
}

PlacesSearchBox.defaultProps = {
    menuPlacement: 'auto'
};

const mapStateToProps = (state, props) => {
    const clientId = clientIdSelector(state, props);
    const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
        state,
        props
    );
    return {
        clientId,
        defaultCountryCenter
    };
};

const withRedux = connect(mapStateToProps);
export default compose(
    withRouter,
    withRedux,
    themr('PlacesSearchBox', theme),
    translate()
)(PlacesSearchBox);
